/* eslint-disable @typescript-eslint/typedef */
import { createSlice } from '@reduxjs/toolkit';

import {
  AddMemberPostsPayload,
  AddPostsPayload,
  AddReactionPayload,
  RemoveReactionPayload,
  ReplacePostPayload,
  SetActivityEngagementPayload,
  SetApprovalCountPayload,
  SetApprovalMembersPayload,
  SetHashtagEngagementPayload,
  SetHashtagOccurrencesPayload,
  SetMemberPostsPayload,
  SetPollResultsPayload,
  SetPostHierarchyPayload,
  SetPostPayload,
  SetPostSearchResultsPayload,
  SetPostsPayload,
  SetProfilePayload,
  SetReactionsPayload,
  SetTribeTemplatePayload,
  SwitchReactionPayload,
  TribeState
} from '../../../types/state/tribe.types';
import { Post, Reaction } from '../../../types/entity.types';

export const initialState: TribeState = {
  tribeTemplate: undefined,
  posts: [],
  memberPosts: [],
  reactions: [],
  post: undefined,
  hashtagOccurrences: [],
  activityEngagement: [],
  hashtagEngagement: [],
  approvalCount: 0,
  approvalMembers: [],
  postSearchResults: undefined,
  postHierarchy: undefined,
  pollResults: undefined,
  profile: undefined
};

const TribeSlice = createSlice({
  name: 'tribe',
  initialState,
  reducers: {
    setTribeTemplate: (state, action: SetTribeTemplatePayload): void => {
      state.tribeTemplate = action.payload;
    },
    setPosts: (state, action: SetPostsPayload): void => {
      state.posts = action.payload;
    },
    setMemberPosts: (state, action: SetMemberPostsPayload): void => {
      state.memberPosts = action.payload;
    },
    setPostSearchResults: (state, action: SetPostSearchResultsPayload): void => {
      state.postSearchResults = action.payload;
    },
    setPostHierarchy: (state, action: SetPostHierarchyPayload): void => {
      state.postHierarchy = action.payload;
    },
    setReactions: (state, action: SetReactionsPayload): void => {
      state.reactions = action.payload;
    },
    addReaction: (state, action: AddReactionPayload): void => {
      state.reactions.push(action.payload);

      const { postExternalReference, reaction } = action.payload;

      const foundPost: Post | undefined = state.posts.find(
        ({ externalReference }: Post): boolean => externalReference === postExternalReference
      );

      if (!foundPost?.reactionCount) throw new Error('Could not add reaction.');

      const foundReaction: Reaction | undefined = foundPost.reactionCount.find(
        ({ text }: Reaction): boolean => text === reaction
      );

      if (foundReaction) {
        foundReaction.count ? foundReaction.count++ : (foundReaction.count = 1);
      } else {
        foundPost.reactionCount?.push({ text: reaction, count: 1 });
      }
    },
    removeReaction: (state, action: RemoveReactionPayload): void => {
      const { postExternalReference, reaction } = action.payload;

      state.reactions = state.reactions.filter((reactionInState: Reaction): boolean => {
        if (reactionInState.postExternalReference === postExternalReference) {
          return reactionInState.reaction !== reaction;
        }

        return true;
      });

      const foundPost: Post | undefined = state.posts.find(
        ({ externalReference }: Post): boolean => externalReference === postExternalReference
      );

      if (!foundPost?.reactionCount) throw new Error('Could not remove reaction.');

      const { reactionCount } = foundPost;

      const foundReactionIndex: number | undefined = reactionCount.findIndex(
        ({ text }: Reaction): boolean => text === reaction
      );

      const reactionToPost: Reaction | undefined = reactionCount[foundReactionIndex];

      if (!reactionToPost?.count) throw new Error('Could not remove reaction.');

      if (reactionToPost.count <= 1) {
        reactionCount.splice(foundReactionIndex, 1);
      } else {
        reactionToPost.count--;
      }
    },
    switchReaction: (state, action: SwitchReactionPayload): void => {
      const { postExternalReference, oldReaction, newReaction } = action.payload;

      state.reactions = state.reactions.filter((reactionInState: Reaction): boolean => {
        if (reactionInState.postExternalReference === postExternalReference) {
          return reactionInState.reaction !== oldReaction;
        }

        return true;
      });

      const foundPost: Post | undefined = state.posts.find(
        ({ externalReference }: Post): boolean => externalReference === postExternalReference
      );

      if (!foundPost?.reactionCount) throw new Error('Could not switch reaction.');

      const { reactionCount } = foundPost;

      const foundReactionIndex: number | undefined = reactionCount.findIndex(
        ({ text }: Reaction): boolean => text === oldReaction
      );

      const reactionToPost: Reaction | undefined = reactionCount[foundReactionIndex];

      if (!reactionToPost?.count) throw new Error('Could not remove reaction.');

      if (reactionToPost.count <= 1) {
        reactionCount.splice(foundReactionIndex, 1);
      } else {
        reactionToPost.count--;
      }

      state.reactions.push({ postExternalReference, reaction: newReaction });

      const foundReaction: Reaction | undefined = reactionCount.find(
        ({ text }: Reaction): boolean => text === newReaction
      );
      if (foundReaction) {
        foundReaction.count ? foundReaction.count++ : (foundReaction.count = 1);
      } else {
        reactionCount.push({ text: newReaction, count: 1 });
      }
    },
    addPosts: (state, action: AddPostsPayload): void => {
      const newPosts: Post[] = action.payload;

      newPosts.forEach((post: Post): void => {
        const foundPost: Post | undefined = state.posts.find(
          ({ externalReference }: Post): boolean => externalReference === post.externalReference
        );

        if (foundPost) return;

        state.posts.push(post);
      });
    },
    addMemberPosts: (state, action: AddMemberPostsPayload): void => {
      const newPosts: Post[] = action.payload;

      newPosts.forEach((post: Post): void => {
        const foundPost: Post | undefined = state.memberPosts.find(
          ({ externalReference }: Post): boolean => externalReference === post.externalReference
        );

        if (foundPost) return;

        state.memberPosts.push(post);
      });
    },
    replacePost: (state, action: ReplacePostPayload): void => {
      const { page, posts } = action.payload;

      state.posts.splice(page, 1, ...posts);
    },
    setPost: (state, action: SetPostPayload): void => {
      state.post = action.payload;
    },
    setHashtagOccurrences: (state, action: SetHashtagOccurrencesPayload): void => {
      state.hashtagOccurrences = action.payload;
    },
    setActivityEngagement: (state, action: SetActivityEngagementPayload): void => {
      state.activityEngagement = action.payload;
    },
    setHashtagEngagement: (state, action: SetHashtagEngagementPayload): void => {
      state.hashtagEngagement = action.payload;
    },
    setApprovalCount: (state, action: SetApprovalCountPayload): void => {
      state.approvalCount = action.payload;
    },
    setApprovalMembers: (state, action: SetApprovalMembersPayload): void => {
      state.approvalMembers = action.payload;
    },
    setPollResults: (state, action: SetPollResultsPayload): void => {
      state.pollResults = action.payload;
    },
    setProfile: (state, action: SetProfilePayload): void => {
      state.profile = action.payload;
    }
  }
});

export const {
  setTribeTemplate,
  setPosts,
  setMemberPosts,
  setReactions,
  addReaction,
  removeReaction,
  switchReaction,
  addPosts,
  addMemberPosts,
  replacePost,
  setPost,
  setHashtagOccurrences,
  setActivityEngagement,
  setHashtagEngagement,
  setApprovalCount,
  setApprovalMembers,
  setPostSearchResults,
  setPostHierarchy,
  setPollResults,
  setProfile
} = TribeSlice.actions;

export default TribeSlice.reducer;
